import { t, getCurrentLanguage } from "../../../useTranslations";
import auditService from "../../../services/AuditService";
import customerRiskService from "../../../services/CustomerRiskService";
import bwraService from "../../../services/BWRAService";
import geoRiskService from "../../../services/GeoRiskService";
import transactionMonitoringService from "../../../services/TransactionMonitoringService";
import periodicReportingService from "../../../services/PeriodicReportingService";
import { countries as staticGeoCountries } from "../../GeoRisk/data/countries";
import { RiskModel } from "../../GeoRisk/models/RiskModel";
import { AMLR_ARTICLES } from "../../BWRA/data/amlrArticles";

export const ACTION_BADGE_KEYS = {
  CREATE: 'create', UPDATE: 'update', DELETE: 'delete',
  LOGIN: 'login', LOGOUT: 'logout',
  DATA_IMPORT: 'dataImport', DATA_EXPORT: 'dataExport',
};

export const RISK_LEVELS = [
  { key: "low", i18nKey: "modules.dashboard.riskLevelLow", min: 0, max: 25 },
  { key: "medium", i18nKey: "modules.dashboard.riskLevelMedium", min: 25, max: 50 },
  { key: "high", i18nKey: "modules.dashboard.riskLevelHigh", min: 50, max: 75 },
  { key: "veryHigh", i18nKey: "modules.dashboard.riskLevelVeryHigh", min: 75, max: 90 },
  { key: "unacceptable", i18nKey: "modules.dashboard.riskLevelUnacceptable", min: 90, max: 100 },
];

const LEVEL_TO_KEY = {
  'Low': 'low', 'Medium': 'medium', 'High': 'high',
  'Very High': 'veryHigh', 'Unacceptable': 'unacceptable',
};

const STORED_LEVEL_TO_KEY = {
  'LOW': 'low', 'MEDIUM': 'medium', 'HIGH': 'high',
  'VERY_HIGH': 'veryHigh', 'UNACCEPTABLE': 'unacceptable',
};

function buildRiskModel(modelConfig) {
  const model = new RiskModel();
  model.configure({
    modelType: modelConfig.modelType || 'deterministic',
    weights: modelConfig.weights || {},
    lowestRiskLevel: modelConfig.lowestRiskLevel || 'Low',
    hybridSettings: modelConfig.hybridSettings,
    ruleGroups: modelConfig.ruleGroups || [],
    riskThresholds: modelConfig.riskThresholds || {},
    riskLevels: modelConfig.riskLevels,
    invertedConfig: modelConfig.invertedConfig,
  });
  return model;
}

function buildCountryDataMap(activeIndices) {
  const countryDataMap = {};
  activeIndices.forEach(idx => {
    geoRiskService.getCountryData(idx.id).forEach(entry => {
      if (!countryDataMap[entry.code]) countryDataMap[entry.code] = {};
      countryDataMap[entry.code][idx.id] = entry.value;
    });
  });
  return countryDataMap;
}

export function getGeoRiskStats() {
  const allIndices = geoRiskService.getIndexes();
  const activeIndices = allIndices.filter(i => i.status === "ACTIVE");
  const countries = geoRiskService.getCountries() || staticGeoCountries;
  const distribution = { low: 0, medium: 0, high: 0, veryHigh: 0, unacceptable: 0, unscored: 0 };

  if (activeIndices.length > 0) {
    const rawConfig = geoRiskService.getModelConfiguration({ modelType: 'deterministic', lowestRiskLevel: 'Low' }) || {};
    const model = buildRiskModel(rawConfig);
    const countryDataMap = buildCountryDataMap(activeIndices);

    countries.forEach(c => {
      const countryData = {};
      activeIndices.forEach(idx => {
        const val = (countryDataMap[c.alpha2] || {})[idx.id];
        if (val !== undefined && val !== null) countryData[idx.id] = val;
      });
      // Always use the model (consistent with CountryTable in GeoRisk module).
      // Falling back to stored c.riskLevel causes over-counting because static
      // country data may have HIGH/VERY_HIGH values that the model would not
      // assign (model returns lowestRiskLevel when no index data triggers).
      const result = model.calculateRisk(countryData, activeIndices, rawConfig.weights || {});
      distribution[LEVEL_TO_KEY[result.level] || 'unscored']++;
    });
  } else {
    countries.forEach(c => {
      const storedKey = STORED_LEVEL_TO_KEY[c.riskLevel];
      if (storedKey) distribution[storedKey]++;
      else distribution.unscored++;
    });
  }

  const highRisk = distribution.high + distribution.veryHigh + distribution.unacceptable;
  return {
    countries: countries.length,
    indices: activeIndices.length,
    totalIndices: allIndices.length,
    highRisk,
    distribution,
    modelConfigured: activeIndices.length > 0,
  };
}

export function getCustomerRiskStats() {
  const models = customerRiskService.getModels();
  const parameters = customerRiskService.getParameters();
  return { models: models.length, parameters: parameters.length, hasModels: models.length > 0 };
}

export function getBwraStats() {
  const risks = bwraService.getRisks();
  const totalControls = risks.reduce((sum, r) => sum + (r.controls ? r.controls.length : 0), 0);
  const mitigated = risks.filter(r => r.controls && r.controls.length > 0).length;
  const unmitigated = risks.length - mitigated;
  let avgGross = 0;
  let avgResidual = 0;
  if (risks.length > 0) {
    avgGross = risks.reduce((sum, r) => {
      const g = r.grossRisk || {};
      return sum + (g.likelihood || 0) * (g.impact || 0);
    }, 0) / risks.length;
    avgResidual = risks.reduce((sum, r) => {
      const res = r.residualRisk || r.grossRisk || {};
      return sum + (res.likelihood || 0) * (res.impact || 0);
    }, 0) / risks.length;
  }
  return {
    risks: risks.length, controls: totalControls, mitigated, unmitigated,
    avgGross: Math.round(avgGross * 10) / 10,
    avgResidual: Math.round(avgResidual * 10) / 10,
    hasData: risks.length > 0,
  };
}

export function getComplianceStats() {
  return bwraService.getGapAnalysis(AMLR_ARTICLES).summary;
}

export function getTxMonStats() {
  return transactionMonitoringService.getStats();
}

export function getPeriodicReportingStats() {
  return periodicReportingService.getStats();
}

export function getDashboardLogs() {
  const allLogs = auditService.getLogs();
  return { recentLogs: allLogs.slice(0, 8), totalLogCount: allLogs.length };
}

export function formatTime(isoString) {
  try {
    const d = new Date(isoString);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return t("modules.dashboard.timeJustNow");
    if (diffMin < 60) return t("modules.dashboard.timeMinutesAgo", { count: diffMin });
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return t("modules.dashboard.timeHoursAgo", { count: diffH });
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return t("modules.dashboard.timeDaysAgo", { count: diffD });
    return d.toLocaleDateString(getCurrentLanguage() === 'sv' ? 'sv-SE' : 'en-GB');
  } catch {
    return "";
  }
}

export function describeActivity(log) {
  const module = log.module || "";
  const action = log.action || "";
  const entity = log.details?.entityType || log.details?.entity || "";
  const name = log.details?.entityId || log.details?.name || "";

  if (action === "LOGIN") return t("modules.dashboard.activityLoggedIn");
  if (action === "LOGOUT") return t("modules.dashboard.activityLoggedOut");

  const verb =
    action === "CREATE" ? t("modules.dashboard.activityCreated")
    : action === "UPDATE" ? t("modules.dashboard.activityUpdated")
    : action === "DELETE" ? t("modules.dashboard.activityDeleted")
    : action === "DATA_IMPORT" ? t("modules.dashboard.activityImported")
    : action === "DATA_EXPORT" ? t("modules.dashboard.activityExported")
    : action;

  const parts = [verb];
  if (entity) {
    const entityKey = entity.toLowerCase().replace(/\s+/g, '');
    const translationPath = `modules.dashboard.entityType.${entityKey}`;
    const translated = t(translationPath);
    parts.push(translated === translationPath ? entity.toLowerCase() : translated);
  }
  if (name) parts.push(`"${name}"`);
  if (module && action !== "LOGIN" && action !== "LOGOUT") {
    const moduleKey = `common.modules.${module}`;
    const moduleTranslated = t(moduleKey);
    parts.push(`${t("modules.dashboard.activityIn")} ${moduleTranslated === moduleKey ? module : moduleTranslated}`);
  }
  return parts.join(" ");
}
